import React from 'react';
import { DetailedItem, FloatingLabelInput } from '../../../../../../ui';
import { useEditableForm } from '../hooks/useEditableForm';
import { SettingsCard } from './SettingsCard';

interface IOrganizationDetails{
  name: string;
  id: string;
  industry: string;
  location: string;
  timeZone: string;
  hasEditEnabled?: boolean;
  onSave?: (data: Omit<IOrganizationDetails, 'hasEditEnabled' | 'onSave'>) => void;
}
export const OrganizationDetails = (props: IOrganizationDetails) => {
  const { name, id, industry, location, timeZone, onSave } = props;

  const { isEditing, formData, isSaving, handleEditToggle, handleSave, handleInputChange } = useEditableForm({
    initialData: { name, id, industry, location, timeZone },
    onSave: async (data) => {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (onSave) {
        onSave(data);
      }
    },
  });

  const icon = (
    <svg className='w-5 h-5 text-blue-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' />
    </svg>
  );

  return (
    <SettingsCard
        title='Organization Details'
        description='Manage your organization information'
        icon={icon}
        isEditing={isEditing}
        isSaving={isSaving}
        onEditToggle={handleEditToggle}
        onSave={handleSave}
    >
      { isEditing ? (
        // Edit Mode
        <>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <FloatingLabelInput
                label='Organization Name'
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
            />
            <FloatingLabelInput
                label='Industry'
                value={formData.industry}
                onChange={(e) => handleInputChange('industry', e.target.value)}
            />
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <FloatingLabelInput
                label='Location'
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
            />
            <FloatingLabelInput
                label='Time Zone'
                value={formData.timeZone}
                onChange={(e) => handleInputChange('timeZone', e.target.value)}
            />
          </div>

          { /* Organization ID - Read Only */ }
          <div className='bg-gray-50 rounded-lg p-4 border border-gray-200'>
            <div className='flex items-center gap-3 mb-2'>
              <svg className='w-4 h-4 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' />
              </svg>
              <span className='text-sm font-medium text-gray-700'>Organization ID</span>
            </div>
            <div className='font-mono text-sm text-gray-900 bg-white px-3 py-2 rounded border'>
              { formData.id }
            </div>
            <p className='text-xs text-gray-500 mt-2'>
              This ID is automatically generated and cannot be changed.
            </p>
          </div>
        </>
      ) : (
        // View Mode
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <div className='p-4 bg-gray-50 rounded-lg'>
            <DetailedItem
                label='Organization Name'
                text={formData.name}
            />
          </div>
          <div className='p-4 bg-gray-50 rounded-lg'>
            <DetailedItem
                label='Industry'
                text={formData.industry}
            />
          </div>
          <div className='p-4 bg-gray-50 rounded-lg'>
            <DetailedItem
                label='Location'
                text={formData.location}
            />
          </div>
          <div className='p-4 bg-gray-50 rounded-lg'>
            <DetailedItem
                label='Time Zone'
                text={formData.timeZone}
            />
          </div>
          <div className='md:col-span-2'>
            <div className='p-4 bg-blue-50 rounded-lg border border-blue-200'>
              <DetailedItem
                  label='Organization ID'
                  text={formData.id}
                  helpertext='This ID is automatically generated and cannot be changed.'
              />
            </div>
          </div>
        </div>
      ) }
    </SettingsCard>
  );
};
